'use client';

import { motion, useReducedMotion } from 'framer-motion';

const EASE = [0.23, 1, 0.32, 1] as const;
const PRESENCE = 0.55;

// Off-centre placement (lower-right) and a size that stays fully inside the
// hero, so the glyph is never clipped. Easy to tune — and when you swap in a
// custom SVG logo, only the <svg> below changes.
const POS = { left: '62%', top: '60%' };
const SIZE = 'min(60vh, 50vw)';

/**
 * The delta glyph in the hero — luminous, fades in from darkness.
 *
 * Positioning lives on a plain wrapper div; the Framer motion is on an inner
 * div. That separation is deliberate: Framer writes `transform` inline (for
 * the scale animation), which would otherwise override Tailwind's centering
 * translate and push the glyph off the hero. With positioning on the wrapper,
 * the glyph stays exactly where intended. The galaxy of dust around it lives
 * in <CosmicBackground />.
 */
export default function DeltaBackdrop() {
  const reduce = useReducedMotion();

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div
        className="absolute"
        style={{
          left: POS.left,
          top: POS.top,
          width: SIZE,
          height: SIZE,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <motion.div
          className="h-full w-full"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, filter: 'blur(14px)' }}
          animate={reduce ? { opacity: PRESENCE } : { opacity: PRESENCE, scale: 1, filter: 'blur(0px)' }}
          transition={{ delay: 1, duration: reduce ? 0.6 : 3, ease: EASE }}
        >
          <svg viewBox="0 0 100 100" className="h-full w-full">
            <defs>
              <linearGradient id="deltaFill" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6ee7c7" />
                <stop offset="55%" stopColor="#34d399" />
                <stop offset="100%" stopColor="#14b8a6" />
              </linearGradient>
              <filter id="d-glow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="1.1" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <text
              id="delta-glyph"
              x="50"
              y="50"
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="'Georgia', 'Times New Roman', serif"
              fontSize="120"
              fill="url(#deltaFill)"
              filter="url(#d-glow)"
            >
              Δ
            </text>
          </svg>
        </motion.div>
      </div>
    </div>
  );
}
